import { equals } from 'ramda';
import type { Editor } from 'slate';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  getEditorTabStateForTabId,
  updateEditorTabStateForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import type { EditorUserState } from 'src/editor/editor-tab-state-registry';

export type { EditorUserState } from 'src/editor/editor-tab-state-registry';

export function getEditorUserStateForTabId(tabId: UUID | null): EditorUserState | null {
  return getEditorTabStateForTabId(tabId)?.documentUi.userState ?? null;
}

export function setEditorUserStateForEditor(
  editor: Editor,
  state: EditorUserState,
  tabId: UUID | null
): void {
  editor.userState = { ...state };
  if (tabId)
    updateEditorTabStateForTabId(tabId, tabState => {
      const isEqual = equals(tabState.documentUi.userState, state);
      if (isEqual) return tabState;

      return {
        ...tabState,
        documentUi: { ...tabState.documentUi, userState: { ...state } },
      };
    });
}

export function deleteEditorUserStateForEditor(editor: Editor, tabId: UUID | null): void {
  delete editor.userState;
  if (tabId)
    updateEditorTabStateForTabId(tabId, tabState => {
      if (!tabState.documentUi.userState) return tabState;

      return {
        ...tabState,
        documentUi: { ...tabState.documentUi, userState: null },
      };
    });
}

export function useGetEditorUserState(): {
  isInSigningCeremony: boolean;
  isReadOnly: boolean;
  isSigned: boolean;
  state: EditorUserState | null;
  userDeclinedSigning: boolean;
} {
  const runtime = useMaybeEditorRuntime();
  const state = useEditorTabStateSelectorForTabId(
    runtime?.tabId ?? null,
    tabState => tabState.documentUi.userState
  );

  return {
    isInSigningCeremony: Boolean(state?.isInSigningCeremony),
    isReadOnly: Boolean(state?.isReadOnly),
    isSigned: Boolean(state?.isSigned),
    state,
    userDeclinedSigning: Boolean(state?.userDeclinedSigning),
  };
}
